import type {
  CanvasConnector,
  CanvasLabeledEdge,
  CanvasSection,
  CanvasShape,
  CanvasTextLabel,
  CanvasTextNote,
  CanvasWidget,
} from "@cohesive/api-entities";
import type {
  BlockLayer,
  Entity,
  LegacyBlockPresentation,
} from "@cohesive/ui-components";
import type { Edge, EdgeProps, Node, NodeProps } from "@xyflow/react";

export type {
  CanvasConnector,
  CanvasLabeledEdge,
  CanvasSection,
  CanvasShape,
  CanvasTextLabel,
  CanvasTextNote,
  CanvasWidget,
};

/** React Flow `NodeProps` for a canvas-native node type. */
export type CanvasNodeProps<
  Data extends Record<string, unknown>,
  Type extends string,
> = NodeProps<Node<Data, Type>>;

/** Loro `canvasShape` node `data` — fields may be partial while editing / legacy snapshots. */
export type CanvasShapeNodeData = Partial<CanvasShape>;

/**
 * Loro `canvasText` node `data` — mirrors toolbox `CanvasTextLabel` (partial
 * while editing). A label's sizing mode is width presence on the NODE, never
 * a field in here: a stored width means fixed (text wraps to it), no stored
 * width means hug (the box follows the text). Labels never store a height.
 */
export type CanvasTextLabelNodeData = Partial<CanvasTextLabel>;

/** Loro `textNote` node `data`. */
export type CanvasTextNoteNodeData = Partial<CanvasTextNote>;

/** Loro `section` node `data` — toolbox `CanvasSection` (partial). */
export type CanvasSectionNodeData = Partial<CanvasSection>;

/** Loro `canvasConnector` node `data` — toolbox `CanvasConnector` (partial). */
export type ConnectorNodeData = Partial<CanvasConnector> & {
  /** @deprecated Legacy local endpoints; prefer `start` / `end`. */
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  /** @deprecated Buggy nested shape from an old `updateConnectorEndpoints`; prefer `start` / `end`. */
  endpoints?: { x1?: number; y1?: number; x2?: number; y2?: number };
  /** @deprecated Combined arrow preset; prefer `start_arrow` / `end_arrow`. */
  arrow_mode?: "none" | "end" | "both";
};

export type ConnectorLocalPoint = { x: number; y: number };

export type ConnectorLocalEndpoints = {
  start: ConnectorLocalPoint;
  end: ConnectorLocalPoint;
};

function readConnectorPoint(value: unknown): ConnectorLocalPoint | null {
  if (!value || typeof value !== "object") return null;
  const p = value as { x?: unknown; y?: unknown };
  if (
    typeof p.x === "number" &&
    typeof p.y === "number" &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y)
  ) {
    return { x: p.x, y: p.y };
  }
  return null;
}

function readLegacyConnectorPoint(
  data: Record<string, unknown>,
  xKey: "x1" | "x2",
  yKey: "y1" | "y2",
): ConnectorLocalPoint | null {
  const x = data[xKey];
  const y = data[yKey];
  if (
    typeof x === "number" &&
    typeof y === "number" &&
    Number.isFinite(x) &&
    Number.isFinite(y)
  ) {
    return { x, y };
  }
  return null;
}

/** Local endpoints inside the React Flow node box (not absolute canvas coords). */
export function resolveConnectorLocalEndpoints(
  data: ConnectorNodeData | Record<string, unknown> | undefined,
  width: number,
  _height: number,
): ConnectorLocalEndpoints {
  const d = (data ?? {}) as Record<string, unknown>;
  const nested = d.endpoints as Record<string, unknown> | undefined;

  const start =
    readConnectorPoint(d.start) ??
    readLegacyConnectorPoint(d, "x1", "y1") ??
    (nested ? readLegacyConnectorPoint(nested, "x1", "y1") : null);

  const end =
    readConnectorPoint(d.end) ??
    readLegacyConnectorPoint(d, "x2", "y2") ??
    (nested ? readLegacyConnectorPoint(nested, "x2", "y2") : null);

  return {
    start: start ?? { x: 0, y: 0 },
    end: end ?? { x: width, y: 0 },
  };
}

/** Persist `start` / `end` and drop legacy endpoint keys from node data. */
export function connectorDataWithLocalEndpoints(
  data: Record<string, unknown>,
  start: ConnectorLocalPoint,
  end: ConnectorLocalPoint,
): Record<string, unknown> {
  const {
    x1: _x1,
    y1: _y1,
    x2: _x2,
    y2: _y2,
    endpoints: _endpoints,
    start: _start,
    end: _end,
    ...rest
  } = data;
  return { ...rest, start, end };
}

/** Loro `entity` node `data`. */
export type EntityNodeData = {
  /**
   * The wrapped entity. Real Liveblocks nodes carry the full entity payload.
   * Upload ghosts and agent placement ghosts carry a *partial* entity whose
   * `type` drives the ghost skeleton (kind / aspect ratio) and whose
   * `data.title` is used as the skeleton label — keeping the ghost data
   * shape isomorphic with the real node so consumers can branch on the
   * same `entity.type`.
   */
  entity?: Entity;
  /**
   * True for Liveblocks-backed loading skeleton nodes. The agent path is
   * written by the backend at tool-call-args-delta time (`PlaceLoadingNode`);
   * the FE writes them for the user-initiated paths (drop, paste,
   * workspace-copy, doc create, URL ingest). Cleared by the backend via
   * ReplaceNodeDataOp on success, or the node is removed entirely on failure.
   */
  loading?: boolean;
  /**
   * Set when URL ingestion fails. The node stays on canvas so the user can
   * see what went wrong, open the original URL, and delete if needed.
   * Mutually exclusive with `loading: true`.
   */
  error?: {
    title: string;
    message?: string;
    /** The original URL that failed to ingest, shown as a clickable link. */
    sourceUrl?: string;
  };
  /**
   * The node's synced copy of the file record's display model — written by
   * the backend at placement so the card routes synchronously from room
   * storage. Read ONLY through blockDisplayFromNodeData (ui-components).
   */
  display?: {
    mode?: "block" | "document";
    chromeless?: boolean;
    layer?: BlockLayer;
  };
  /**
   * Legacy block marker (its presence meant "block"), still present on
   * canvases and published snapshots from before `display` existed. Read
   * ONLY through blockDisplayFromNodeData, which maps it permanently.
   */
  presentation?: LegacyBlockPresentation;
};

/**
 * Loro `canvasDrawing` node `data` — one freehand ink stroke. Points are a flat
 * `[x0, y0, x1, y1, …]` list in node-local coordinates (flat so the whole
 * stroke is one Liveblocks value rather than a list of nested objects).
 *
 * Hand-written rather than imported from `@cohesive/api-entities`: the
 * generated entity mirrors the toolbox `canvas-drawing.json` author-side shape
 * (absolute `{x, y}` points), not the shape the node stores.
 */
export type CanvasDrawingNodeData = {
  points?: number[];
  color?: string;
  stroke_width?: number;
  /** Pen tip — see `drawingTipInk` for how it renders. */
  tip?: string;
};

/** Loro `widgetNode` node `data` — `CanvasWidget` allows preset-specific keys. */
export type WidgetNodeData = Partial<CanvasWidget>;

/** React Flow `labeled` edge `data`. */
export type LabeledEdgeData = Partial<CanvasLabeledEdge>;

/** Typography of a line label (edge or free connector) — the agent contract's shape. */
export type LineLabelStyle = NonNullable<CanvasLabeledEdge["label_style"]>;

export type CanvasShapeNodeProps = CanvasNodeProps<
  CanvasShapeNodeData,
  "canvasShape"
>;
export type CanvasTextLabelNodeProps = CanvasNodeProps<
  CanvasTextLabelNodeData,
  "canvasText"
>;
export type CanvasTextNoteNodeProps = CanvasNodeProps<
  CanvasTextNoteNodeData,
  "textNote"
>;
export type CanvasSectionNodeProps = CanvasNodeProps<
  CanvasSectionNodeData,
  "section"
>;
export type ConnectorNodeProps = CanvasNodeProps<
  ConnectorNodeData,
  "canvasConnector"
>;
export type EntityNodeProps = CanvasNodeProps<EntityNodeData, "entity">;
export type CanvasDrawingNodeProps = CanvasNodeProps<
  CanvasDrawingNodeData,
  "canvasDrawing"
>;
export type WidgetNodeProps = CanvasNodeProps<WidgetNodeData, "widgetNode">;

/** Loro / RF payload for `chatSession` nodes (includes optional `userId` for per-user visibility). */
export type CanvasChatSessionNodeData = {
  flowSessionId: string;
  status?: string;
  firstMessage?: string;
  userId?: string;
  /** Whether the quick chat panel is expanded. Persisted so the open/collapsed
   *  state survives reloads. Safe to store on shared canvas data because chat
   *  session nodes are per-user (gated by `userId`). */
  expanded?: boolean;
};

export type CanvasChatSessionNodeProps = CanvasNodeProps<
  CanvasChatSessionNodeData,
  "chatSession"
>;

export type LabeledRFEdge = Edge<LabeledEdgeData, "labeled">;
export type LabeledEdgeProps = EdgeProps<LabeledRFEdge>;

export const CANVAS_STROKE_WIDTHS = [1, 2, 4] as const;

export type CanvasStrokeWidth = (typeof CANVAS_STROKE_WIDTHS)[number];

/** Coerce arbitrary persisted stroke width to the toolbox enum (1, 2, 4). */
export function normalizeCanvasStrokeWidth(value: unknown): CanvasStrokeWidth {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 2;
  if (n <= 1) return 1;
  if (n >= 4) return 4;
  return 2;
}

const TEXT_LABEL_FONT_FAMILIES: NonNullable<CanvasTextLabel["font_family"]>[] =
  [
    "Inter, sans-serif",
    "Georgia, serif",
    "'Courier New', monospace",
    "'Comic Sans MS', cursive",
  ];

/** Free px font sizes, clamped to what stays legible and renderable on canvas. */
export const MIN_FONT_SIZE_PX = 8;
export const MAX_FONT_SIZE_PX = 400;
export const DEFAULT_TEXT_LABEL_FONT_SIZE = "16px";

/**
 * Text inset and empty-state prompt of a text label. The placement ghost
 * repeats them so the preview text sits exactly where the label's own text
 * will — nothing shifts between hovering, dropping and typing.
 */
export const TEXT_LABEL_TEXT_INSET_X = 6;
export const TEXT_LABEL_TEXT_INSET_Y = 4;
export const TEXT_LABEL_TEXT_PADDING = `${TEXT_LABEL_TEXT_INSET_Y}px ${TEXT_LABEL_TEXT_INSET_X}px`;
export const TEXT_LABEL_PLACEHOLDER = "Add text";
export const TEXT_LABEL_PLACEHOLDER_COLOR = "hsl(var(--foreground) / 0.55)";

export function clampFontSizePx(px: number): number {
  return Math.min(MAX_FONT_SIZE_PX, Math.max(MIN_FONT_SIZE_PX, Math.round(px)));
}

/** Read a CSS font size as px, falling back to the label default. */
export function parseFontSizePx(raw: string | undefined): number {
  const px = parseFloat(raw ?? "");
  if (!Number.isFinite(px) || px <= 0) {
    return parseFloat(DEFAULT_TEXT_LABEL_FONT_SIZE);
  }
  return clampFontSizePx(px);
}

/** Map persisted / UI font family to a toolbox `CanvasTextLabel.font_family` value. */
export function normalizeTextLabelFontFamily(
  raw: string | undefined,
): NonNullable<CanvasTextLabel["font_family"]> {
  if (raw && (TEXT_LABEL_FONT_FAMILIES as string[]).includes(raw)) {
    return raw as NonNullable<CanvasTextLabel["font_family"]>;
  }
  if (raw?.includes("Georgia")) return "Georgia, serif";
  if (raw?.includes("Courier")) return "'Courier New', monospace";
  if (raw?.includes("Comic")) return "'Comic Sans MS', cursive";
  return "Inter, sans-serif";
}

/**
 * Coerce a persisted / UI font size to a storable px string. Font size is a
 * free value — this only clamps it into range; it never snaps to a preset.
 */
export function normalizeTextLabelFontSize(raw: string | undefined): string {
  return `${parseFontSizePx(raw)}px`;
}
